// Opportunity lifecycle events (ADV-145) — the timestamped historical moments
// of an opportunity's progression (created → proposal → job → invoice → close).
// This is the "moment" axis; the linked-records panels are the "current state"
// axis (decision T2 — two axes, never duplication).
//
// Slice 1 DERIVES the feed client-side from the opportunity + its already-
// fetched child records (no new endpoint — decision-noted in the spec as
// "consumes an existing/forthcoming lifecycle-events endpoint"). Slice 2 swaps
// the deriver for a server lifecycle-events endpoint without touching the
// presentational timeline, which is purely props-driven over LifecycleEvent[].

export type LifecycleEventType =
  | "created"
  | "proposal"
  | "job"
  | "invoice"
  | "won"
  | "lost";

export interface LifecycleEvent {
  id: string;
  type: LifecycleEventType;
  /** ISO timestamp; the feed is sorted ascending by this. */
  timestamp: string;
  title: string;
  /** Optional secondary line (amount, status, address…). */
  detail?: string;
}

type Row = Record<string, unknown>;

function str(row: Row, ...keys: string[]): string | undefined {
  for (const k of keys) {
    const v = row[k];
    if (typeof v === "string" && v.trim()) return v;
  }
  return undefined;
}

function num(row: Row, ...keys: string[]): number | undefined {
  for (const k of keys) {
    const v = row[k];
    if (typeof v === "number") return v;
  }
  return undefined;
}

function money(row: Row, ...keys: string[]): string | undefined {
  const v = num(row, ...keys);
  if (v == null) return undefined;
  return v.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

/**
 * Derive an opportunity's lifecycle feed from its row + child records. Each
 * event needs a usable timestamp (rows without one are skipped — an undated
 * milestone has no place on a chronological axis). Returns the events sorted
 * oldest-first; the timeline renders them top-down newest-or-oldest at the
 * call site's discretion.
 */
export function deriveOpportunityLifecycle(
  opportunity: Row | undefined,
  proposals: Row[],
  jobs: Row[],
  invoices: Row[],
): LifecycleEvent[] {
  const events: LifecycleEvent[] = [];

  if (opportunity) {
    const created = str(opportunity, "created_at", "lead_date");
    if (created) {
      events.push({
        id: `opp-created-${opportunity.id as string}`,
        type: "created",
        timestamp: created,
        title: "Opportunity created",
        detail: str(opportunity, "name"),
      });
    }
    const stage = str(opportunity, "stage");
    // Canonical close date is sold_date (OpportunitySchema, nullable);
    // updated_at is the last-resort fallback for legacy rows.
    const closedAt = str(opportunity, "sold_date", "updated_at");
    if (stage === "closed_won" && closedAt) {
      events.push({
        id: `opp-won-${opportunity.id as string}`,
        type: "won",
        timestamp: closedAt,
        title: "Closed won",
        detail: money(opportunity, "proposal_value"),
      });
    } else if (stage === "closed_lost" && closedAt) {
      events.push({
        id: `opp-lost-${opportunity.id as string}`,
        type: "lost",
        timestamp: closedAt,
        title: "Closed lost",
      });
    }
  }

  for (const p of proposals) {
    const status = str(p, "status");
    // ProposalSchema's enum value is "approved" (with an approved_at stamp) —
    // approved proposals are dated by approval, not by when they were sent.
    const ts =
      status === "approved"
        ? str(p, "approved_at", "sent_at", "created_at")
        : str(p, "sent_at", "created_at");
    if (!ts) continue;
    events.push({
      id: `proposal-${p.id as string}`,
      type: "proposal",
      timestamp: ts,
      title:
        status === "approved"
          ? "Proposal approved"
          : status === "sent"
            ? "Proposal sent"
            : "Proposal drafted",
      detail: money(p, "final_price", "total", "grand_total", "amount"),
    });
  }

  for (const j of jobs) {
    const status = str(j, "status");
    // Completed jobs are dated by JobSchema's completed_at when present.
    const ts =
      status === "completed"
        ? str(j, "completed_at", "scheduled_date", "created_at")
        : str(j, "scheduled_date", "created_at");
    if (!ts) continue;
    events.push({
      id: `job-${j.id as string}`,
      type: "job",
      timestamp: ts,
      title: status === "completed" ? "Job completed" : "Job scheduled",
      detail: str(j, "job_number", "customer_name"),
    });
  }

  for (const inv of invoices) {
    const status = str(inv, "status");
    // InvoiceSchema dates: issue_date for issued, paid_at for paid invoices.
    const ts =
      status === "paid"
        ? str(inv, "paid_at", "issue_date", "created_at")
        : str(inv, "issue_date", "created_at");
    if (!ts) continue;
    events.push({
      id: `invoice-${inv.id as string}`,
      type: "invoice",
      timestamp: ts,
      title: status === "paid" ? "Invoice paid" : "Invoice issued",
      detail: money(inv, "total_amount", "total", "amount_due", "amount"),
    });
  }

  return events.sort((a, b) =>
    a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0,
  );
}
